/**
 * permissions.js — client-side employee/capability helpers.
 *
 * The backend is the source of truth (it enforces every read/write); these
 * helpers only drive UX (hide nav items, disable controls). They read the
 * `collaboratorRole` / `permissions` fields the API adds to `/auth/me`
 * (see `_serialize_user` in api/auth_views.py).
 *
 * There is no operational/read_only role anymore — an account is either the
 * tenant OWNER or an EMPLOYEE defined entirely by a granular capability map.
 *
 * Backward-compatible: a user object without these fields (older cached
 * payload) is treated as an owner with full permissions, so nothing breaks.
 */
import { useAuth } from '@/lib/AuthContext';

export const ROLE_OWNER = 'owner';
export const ROLE_EMPLOYEE = 'employee';

export function roleOf(user) {
  return user?.collaboratorRole || ROLE_OWNER;
}

export function isOwner(user) {
  return roleOf(user) === ROLE_OWNER;
}

export function isCollaborator(user) {
  return !!user?.isCollaborator;
}

/**
 * Does the user have `capability`? Owners (and legacy users without a
 * `permissions` map) get everything; employees get the server-computed map.
 */
export function can(user, capability) {
  if (!user) return false;
  if (isOwner(user)) return true; // owners have every capability (also robust
  // against a stale /auth/me payload missing newly-added capability keys)
  const perms = user.permissions;
  if (!perms) return true; // legacy payload without an explicit map
  return !!perms[capability];
}

/**
 * Los locales a los que llega este usuario, o `null` si a todos.
 *
 * `null` y no `[]`, que es lo que significaria "a ninguno". Un payload de
 * `/auth/me` viejo (sin el campo) tambien da `null`: sin acotar, que es lo que
 * eran todos los empleados antes de esto.
 *
 * **No sirve para filtrar nada.** Lo que ve cada quien ya viene recortado del
 * servidor -es el, y no esto, quien manda-. Esto existe para poder DECIRLO: sin
 * el, un empleado acotado a un local cuyo token de Google se cayo ve la misma
 * pantalla vacia que quien no ha conectado nunca, y el aviso le ofrece conectar,
 * que es cosa del titular.
 */
export function allowedLocationIds(user) {
  const ids = user?.allowedLocationIds;
  return Array.isArray(ids) ? ids : null;
}

/** Le han acotado los locales a este usuario? Un titular NUNCA lo esta. */
export function isLocationScoped(user) {
  return allowedLocationIds(user) !== null;
}

/** Hook form: `const { can, isOwner, isCollaborator } = usePermissions();` */
export function usePermissions() {
  const { user } = useAuth();
  return {
    user,
    role: roleOf(user),
    isOwner: isOwner(user),
    isCollaborator: isCollaborator(user),
    isLocationScoped: isLocationScoped(user),
    allowedLocationIds: allowedLocationIds(user),
    can: (capability) => can(user, capability),
  };
}
